import { Socket } from 'socket.io-client';
import { BaseSocketService, SocketConnectionConfig } from './baseSocketService';
import { Application } from '../graphql/types';

export interface ApplicationEvent {
  type: 'created' | 'updated' | 'deleted';
  application: Application;
}
export interface ApplicationCallbacks {
  onCreated?: (event: ApplicationEvent) => void;
  onDeleted?: (event: ApplicationEvent) => void;
  onUpdated?: (event: ApplicationEvent) => void;
}

export class ApplicationSocketService extends BaseSocketService {
  private listeners: Map<string, Map<string, Function>> = new Map();
  private subscriptions: Map<string, number> = new Map();

  constructor() {
    super('ApplicationUpdates');
  }

  async subscribeTo(applicationId: string | null, config: SocketConnectionConfig, callbacks: ApplicationCallbacks): Promise<void> {
    const socket = await this.getSocket(config);

    if (this.subscriptions.size === 0) {
      socket.on('exception', (error) => {
        this.logger.error('Server exception:', error);
      });
    }

    const subscriptionKey = applicationId || 'all';
    const currentCount = this.subscriptions.get(subscriptionKey) || 0;

    if (currentCount === 0) {
      const subscribeData = applicationId ? { applicationId } : { subscribeToAll: true };

      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Subscription request timed out'));
        }, 5000);

        socket.once('subscribed', () => {
          clearTimeout(timeoutId);
          resolve();
        });

        socket.once('error', (error) => {
          clearTimeout(timeoutId);
          reject(error);
        });

        socket.emit('subscribe', subscribeData);
      });
    }

    this.subscriptions.set(subscriptionKey, currentCount + 1);
    if (currentCount === 0) {
      this.incrementSubscriptions();
    }
    this.setupListeners(socket, applicationId, callbacks, subscriptionKey);
  }

  async unsubscribe(applicationId: string | null): Promise<void> {
    const subscriptionKey = applicationId || 'all';
    const currentCount = this.subscriptions.get(subscriptionKey) || 0;

    if (currentCount <= 1) {
      this.subscriptions.delete(subscriptionKey);
      this.decrementSubscriptions();
      this.removeListeners(subscriptionKey);

      if (this.socket?.connected) {
        const unsubscribeData = applicationId ? { applicationId } : { unsubscribeFromAll: true };
        await this.emitWithAuth('unsubscribe', unsubscribeData, {
          namespace: 'application-updates',
          userId: localStorage.getItem('userId') || ''
        });
      }
    } else {
      this.subscriptions.set(subscriptionKey, currentCount - 1);
      this.removeListeners(subscriptionKey);
    }
  }

  protected onReconnection(): void {
    if (this.socket && this.subscriptions.size > 0) {
      this.subscriptions.forEach((_, subscriptionKey) => {
        const applicationId = subscriptionKey === 'all' ? null : subscriptionKey;
        const subscribeData = applicationId ? { applicationId } : { subscribeToAll: true };
        this.emitWithAuth('subscribe', subscribeData, { namespace: 'application-updates', userId: localStorage.getItem('userId') || '' });
      });
    }
  }

  private setupListeners(socket: Socket, applicationId: string | null, callbacks: ApplicationCallbacks, subscriptionKey: string): void {
    if (!this.listeners.has(subscriptionKey)) {
      this.listeners.set(subscriptionKey, new Map());
    }
    const applicationListeners = this.listeners.get(subscriptionKey)!;
    if (callbacks.onCreated) {
      const handler = (data: ApplicationEvent) => {
        if (!data?.application) return;
        if (applicationId && data.application.id !== applicationId) return;
        callbacks.onCreated!(data);
      };
      socket.on('application:created', handler);
      applicationListeners.set('application:created', handler);
    }

    if (callbacks.onUpdated) {
      const handler = (data: ApplicationEvent) => {
        if (!data?.application) {
          this.logger.debug('Received application:updated event with no Application data');
          return;
        }
        this.logger.debug(
          `Received application:updated for Application ${data.application.id}, subscription applicationId: ${applicationId || 'all'}`
        );
        if (applicationId && data.application.id !== applicationId) {
          this.logger.debug(`Discarding application:updated for Application ${data.application.id} (does not match ${applicationId})`);
          return;
        }
        callbacks.onUpdated!(data);
      };
      socket.on('application:updated', handler);
      applicationListeners.set('application:updated', handler);
    }
    if (callbacks.onDeleted) {
      const handler = (data: ApplicationEvent) => {
        if (!data?.application) return;
        if (applicationId && data.application.id !== applicationId) return;
        callbacks.onDeleted!(data);
      };
      socket.on('application:deleted', handler);
      applicationListeners.set('application:deleted', handler);
    }
  }

  private removeListeners(subscriptionKey: string): void {
    if (!this.socket || !this.listeners.has(subscriptionKey)) return;

    const applicationListeners = this.listeners.get(subscriptionKey)!;
    for (const [event, handler] of applicationListeners.entries()) {
      this.socket.off(event, handler as any);
    }
    this.listeners.delete(subscriptionKey);
  }
}

const applicationSocketService = new ApplicationSocketService();
export default applicationSocketService;
